import React, { useState } from 'react';
import { DateTime } from 'luxon';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  Chip,
  Paper,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ErrorIcon from '@mui/icons-material/Error';

interface StatusHistoryItem {
  status: string;
  timestamp: string;
  reason?: string;
  _id: string;
}

interface ActionMetadata {
  dealer_id?: string;
  lead_type?: string;
  ingestion_type?: string;
  ingestion_provider?: string;
  provider?: string;
  run_status?: string;
  metrics?: {
    no_of_records?: number;
    content_type?: string;
  };
  runId?: string;
  actionId?: string;
  dataType?: string;
  expiresAt?: string;
  conversionMetadata?: any;
  timestamp?: string;
  size?: number;
  mapperName?: string;
  no_of_mapped_data?: number;
  no_of_records?: number;
  contentType?: string;
  originalType?: string;
  airflow_logs?: any[];
}

interface Action {
  _id: string;
  function_name: string;
  parameters: any;
  status: string;
  status_history: StatusHistoryItem[];
  metadata: ActionMetadata;
  is_data_available_to_download: boolean;
}

interface ActionsAccordionComponentProps {
  actions: Action[];
  title?: string;
  runId?: string; // Add runId prop
  onDownload?: (runId: string, actionId: string, action: Action, format?: string) => void;
}

const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />;
    case 'running':
      return <PlayArrowIcon sx={{ color: '#2196f3', fontSize: 20 }} />;
    case 'pending':
      return <PendingIcon sx={{ color: '#ff9800', fontSize: 20 }} />;
    case 'failed':
      return <ErrorIcon sx={{ color: '#f44336', fontSize: 20 }} />;
    default:
      return <PendingIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />;
  }
};

const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'completed':
      return '#4caf50';
    case 'running':
      return '#2196f3';
    case 'pending':
      return '#ff9800';
    case 'failed':
      return '#f44336';
    default:
      return '#9e9e9e';
  }
};

const formatDuration = (startTime: string, endTime: string) => {
  const start = DateTime.fromISO(startTime);
  const end = DateTime.fromISO(endTime);
  const duration = end.diff(start);
  
  const minutes = Math.floor(duration.as('minutes'));
  const seconds = Math.floor(duration.as('seconds') % 60);
  
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

export const ActionsAccordionMAIQ: React.FC<ActionsAccordionComponentProps> = ({
  actions,
  title = 'Pipeline Actions',
  runId,
  onDownload,
}) => {
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);

  const handleAccordionChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const handleDefaultDownload = (action: Action, event?: React.MouseEvent<HTMLElement>) => {
    // Prevent accordion expansion
    if (event) {
      event.stopPropagation();
    }
    
    console.log('=== ACTIONS ACCORDION DEFAULT DOWNLOAD CLICKED ===');
    console.log('Action clicked:', action);
    console.log('Action ID:', action._id);
    console.log('Run ID:', runId);
    console.log('Action parameters:', action.parameters);
    console.log('onDownload function available:', !!onDownload);
    
    // Get default format from action parameters
    const defaultFormat = action.parameters?.data_type || action.parameters?.content_type || 'json';
    console.log('Default format from parameters:', defaultFormat);
    
    if (onDownload && action._id && runId) {
      console.log('✅ Calling onDownload with default format:', { runId, actionId: action._id, action, format: defaultFormat });
      onDownload(runId, action._id, action, defaultFormat);
    } else {
      console.error('❌ Missing required data for download:');
      console.error('- onDownload function:', !!onDownload);
      console.error('- action ID:', action._id);
      console.error('- run ID:', runId);
      console.error('Full action object:', action);
      alert('Error: Missing required information for download.');
    }
  };

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>, action: Action) => {
    event.stopPropagation();
    setDownloadMenuAnchor(event.currentTarget);
    setSelectedAction(action);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
    setSelectedAction(null);
  };

  const handleDownload = (format: string) => {
    console.log('=== ACTIONS ACCORDION FORMAT DOWNLOAD CLICKED ===');
    console.log('Action clicked:', selectedAction);
    console.log('Format selected:', format);
    console.log('Action ID:', selectedAction?._id);
    console.log('Run ID:', runId);
    console.log('Action parameters:', selectedAction?.parameters);
    console.log('onDownload function available:', !!onDownload);
    
    if (onDownload && selectedAction?._id && runId) {
      console.log('✅ Calling onDownload with format:', { runId, actionId: selectedAction._id, action: selectedAction, format });
      onDownload(runId, selectedAction._id, selectedAction, format);
    } else {
      console.error('❌ Missing required data for download:');
      console.error('- onDownload function:', !!onDownload);
      console.error('- action ID:', selectedAction?._id);
      console.error('- run ID:', runId);
      console.error('Full action object:', selectedAction);
      alert('Error: Missing required information for download.');
    }
    
    handleDownloadMenuClose();
  };

  const handleDownloadButtonClick = (event: React.MouseEvent<HTMLElement>, action: Action) => {
    event.stopPropagation();
    
    // Check if click was on the dropdown arrow part
    const target = event.target as HTMLElement;
    const isDropdownClick = target.closest('[data-dropdown="true"]') || 
                           target.classList.contains('MuiSvgIcon-root') && 
                           target.textContent === 'keyboard_arrow_down';
    
    if (isDropdownClick) {
      // Open dropdown menu
      setDownloadMenuAnchor(event.currentTarget);
      setSelectedAction(action);
    } else {
      // Direct download with default format
      handleDefaultDownload(action, event);
    }
  };

  const renderStatusHistory = (statusHistory: StatusHistoryItem[]) => {
    // Check if statusHistory exists and is not null/undefined
    if (!statusHistory || !Array.isArray(statusHistory) || statusHistory.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
          Status History
        </Typography>
        <Box sx={{ position: 'relative' }}>
          {statusHistory.map((item, index) => {
            const isLast = index === statusHistory.length - 1;
            const nextItem = statusHistory[index + 1];
            const duration = nextItem 
              ? formatDuration(item.timestamp, nextItem.timestamp)
              : null;

            return (
              <Box key={item._id} sx={{ position: 'relative', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Status Icon */}
                  <Box sx={{ mt: 0.5 }}>
                    {getStatusIcon(item.status)}
                  </Box>
                  
                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </Typography>
                      {duration && (
                        <Chip
                          label={duration}
                          size="small"
                          sx={{
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            height: '20px',
                          }}
                        />
                      )}
                    </Box>
                    
                    <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                      {DateTime.fromISO(item.timestamp).toFormat('dd LLL yyyy hh:mm a')}
                    </Typography>
                    
                    {item.reason && (
                      <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mt: 0.5 }}>
                        {item.reason}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                {/* Connecting Line */}
                {!isLast && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '10px',
                      top: '24px',
                      width: '2px',
                      height: 'calc(100% + 8px)',
                      backgroundColor: '#e5e7eb',
                      zIndex: 0,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderMetadata = (metadata: ActionMetadata) => {
    // Check if metadata exists and is not null/undefined
    if (!metadata) {
      return null;
    }

    const metadataItems: Array<{ label: string; value: string }> = [];
    
    // Only show the fields that were mentioned in the requirements
    if (metadata.provider) metadataItems.push({ label: 'Provider', value: metadata.provider });
    if (metadata.lead_type) metadataItems.push({ label: 'Lead Type', value: metadata.lead_type });
    if (metadata.ingestion_type) metadataItems.push({ label: 'Ingestion Type', value: metadata.ingestion_type });
    if (metadata.ingestion_provider) metadataItems.push({ label: 'Ingestion Provider', value: metadata.ingestion_provider });
    if (metadata.run_status) metadataItems.push({ label: 'Run Status', value: metadata.run_status });
    if (metadata.dataType) metadataItems.push({ label: 'Data Type', value: metadata.dataType });
    if (metadata.mapperName) metadataItems.push({ label: 'Mapper Name', value: metadata.mapperName });
    if (metadata.size) metadataItems.push({ label: 'Size', value: `${(metadata.size / 1024).toFixed(2)} KB` });
    if (metadata.no_of_records) metadataItems.push({ label: 'Records', value: metadata.no_of_records.toString() });
    if (metadata.no_of_mapped_data) metadataItems.push({ label: 'Mapped Records', value: metadata.no_of_mapped_data.toString() });
    if (metadata.expiresAt) metadataItems.push({ 
      label: 'Expires At', 
      value: DateTime.fromISO(metadata.expiresAt).toFormat('dd LLL yyyy hh:mm a') 
    });

    // Dynamically render metrics if available
    if (metadata.metrics && typeof metadata.metrics === 'object') {
      Object.entries(metadata.metrics).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          // Convert snake_case to Title Case (e.g., "no_of_leads" -> "No Of Leads")
          const label = key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          
          metadataItems.push({ 
            label, 
            value: typeof value === 'number' ? value.toString() : String(value) 
          });
        }
      });
    }

    if (metadataItems.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
          Metadata
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          {metadataItems.map((item, index) => (
            <Box key={index}>
              <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#374151' }}>
        {title}
      </Typography>
      
      {(!actions || !Array.isArray(actions) || actions.length === 0) ? (
        <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 4 }}>
          No actions available
        </Typography>
      ) : (
        actions.map((action, index) => {
        // Skip rendering if action is invalid or missing required properties
        if (!action || !action._id || !action.function_name) {
          return null;
        }

        return (
          <Accordion
            key={action._id}
            expanded={expandedAccordion === action._id}
            onChange={handleAccordionChange(action._id)}
            sx={{
              mb: 2,
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              '&:before': { display: 'none' },
              '&.Mui-expanded': {
                margin: '16px 0',
              },
            }}
          >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: '#f9fafb',
              borderBottom: expandedAccordion === action._id ? '1px solid #e5e7eb' : 'none',
              '&.Mui-expanded': {
                backgroundColor: '#ffffff',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getStatusIcon(action.status)}
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#374151' }}>
                    {action.function_name.charAt(0).toUpperCase() + action.function_name.slice(1)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    {action.status.charAt(0).toUpperCase() + action.status.slice(1)}
                  </Typography>
                </Box>
              </Box>
              
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                 <Chip
                   label={action.status}
                   size="small"
                   sx={{
                     backgroundColor: getStatusColor(action.status) + '20',
                     color: getStatusColor(action.status),
                     fontWeight: 600,
                   }}
                 />
                 {action.is_data_available_to_download && (
                   <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                     <Tooltip title="Download Data (Default Format)">
                       <IconButton
                         size="small"
                         onClick={(e) => handleDefaultDownload(action, e)}
                         sx={{
                           color: '#0F2A52',
                           border: '1px solid #0F2A52',
                           borderRight: 'none',
                           borderRadius: '6px 0 0 6px',
                           padding: '4px 8px',
                           '&:hover': {
                             backgroundColor: 'rgba(15, 42, 82, 0.04)',
                             borderColor: '#0F2A52',
                           },
                         }}
                       >
                         <DownloadIcon fontSize="small" />
                       </IconButton>
                     </Tooltip>
                     <Tooltip title="More Download Options">
                       <IconButton
                         size="small"
                         onClick={(e) => handleDropdownClick(e, action)}
                         data-dropdown="true"
                         sx={{
                           color: '#0F2A52',
                           border: '1px solid #0F2A52',
                           borderRadius: '0 6px 6px 0',
                           padding: '4px 8px',
                           '&:hover': {
                             backgroundColor: 'rgba(15, 42, 82, 0.04)',
                             borderColor: '#0F2A52',
                           },
                         }}
                       >
                         <KeyboardArrowDownIcon fontSize="small" />
                       </IconButton>
                     </Tooltip>
                   </Box>
                 )}
               </Box>
            </Box>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 3 }}>
            {renderMetadata(action.metadata)}
            {renderStatusHistory(action.status_history)}
          </AccordionDetails>
        </Accordion>
        );
      })
      )}
      
      {/* Download Format Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 120,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        }}
      >
        <MenuItem 
          onClick={() => handleDownload('json')}
          sx={{
            fontSize: '0.875rem',
            py: 1.5,
            '&:hover': {
              backgroundColor: 'rgba(15, 42, 82, 0.04)',
            },
          }}
        >
          <DownloadIcon fontSize="small" sx={{ mr: 1, color: '#0F2A52' }} />
          Download JSON
        </MenuItem>
        <MenuItem 
          onClick={() => handleDownload('csv')}
          sx={{
            fontSize: '0.875rem',
            py: 1.5,
            '&:hover': {
              backgroundColor: 'rgba(15, 42, 82, 0.04)',
            },
          }}
        >
          <DownloadIcon fontSize="small" sx={{ mr: 1, color: '#0F2A52' }} />
          Download CSV
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ActionsAccordionMAIQ; 